import { useMemo } from 'react';
import numericComparer from '@elementary/common-ui/utils/comparers/numericComparer';
import { ModelRuns } from '@elementary/common-ui/types';
import modelRunsTotalsRenderer from '@elementary/common-ui/components/cellRenderers/modelRunsTotalsRenderer';
import modelRunsTotalsComparer from '@elementary/common-ui/utils/comparers/modelRunsTotalsComparer';
import statusFilterRenderer from '@elementary/common-ui/components/cellRenderers/statusFilterRenderer';
import StatusChip from '@elementary/common-ui/components/Chip/StatusChip';
import { capitalize, distinctArray } from '@elementary/common-ui/utils/common';
import execChangeRenderer from '@elementary/common-ui/components/cellRenderers/execChangeRenderer';
import execTimeRenderer from '@elementary/common-ui/components/cellRenderers/execTimeRenderer';
import {
  AvatarGroup,
  ChipCounterList,
  Column,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import { ColumnFilterOption } from '@elementary/common-ui/components/Table/types';
import { getFilterOptionsOverride } from '@elementary/common-ui/utils/graphqlFilters';
import { resultsColumnFilterMatcher } from '../utils';

const getItemUniqueFilterOptions = (
  tableData: ModelRuns[],
  fieldPath: keyof NonNullable<ModelRuns['asset_metadata']>,
) => {
  const allItems =
    tableData
      ?.flatMap((item) => item?.asset_metadata?.[fieldPath] || '')
      .filter((val) => val !== '') || [];
  const distinctItems = distinctArray(allItems as string[]);
  return distinctItems.map((item) => ({ value: item, label: item }));
};

const useModelRunsTableColumns = ({
  tableData,
  isSaaS = false,
  filterOptions,
}: {
  tableData: ModelRuns[] | undefined;
  isSaaS?: boolean;
  filterOptions?: Record<string, ColumnFilterOption[]> | null;
}) => {
  const maxExecTime = useMemo(
    () =>
      tableData?.map
        ? Math.max(...tableData.map((item) => Number(item.last_exec_time) || 0))
        : 0,
    [tableData],
  );

  const resultsColumnFilterOptions = useMemo(() => {
    if (isSaaS) {
      // The Results column has no server-side equivalent (it's derived from
      // errorCount/successCount aggregates), so we expose fixed labels.
      return [
        { value: 'errors', label: 'Errors' },
        { value: 'success', label: 'Success' },
      ];
    }

    const allStatuses =
      tableData
        ?.map((item) =>
          Object.keys(item.totals).filter(
            (key) => item.totals[key as 'errors' | 'success'] > 0,
          ),
        )
        .flat() || [];
    const distinct = distinctArray(allStatuses);
    return distinct.map((val) => ({ value: val, label: capitalize(val) }));
  }, [isSaaS, tableData]);

  const columns: Array<Column<ModelRuns>> = useMemo(() => {
    const baseColumns: Array<Column<ModelRuns>> = [
      { id: 'database_name', header: 'Database', defaultUntoggled: true },
      { id: 'schema', header: 'Schema' },
      {
        id: 'name',
        header: 'Name',
        nonToggleable: true,
        filterDropdownWidth: 350,
      },
      {
        id: 'last_exec_time',
        header: 'Last exec. time',
        filterable: false,
        renderer: (rowData) =>
          execTimeRenderer(rowData.last_exec_time, maxExecTime),
        compareFunc: (a, b) => numericComparer(a, b, 'last_exec_time'),
        nonToggleable: true,
      },
      {
        id: 'median_exec_time',
        header: 'Median exec. time',
        filterable: false,
        renderer: (rowData) => execTimeRenderer(rowData.median_exec_time),
        compareFunc: (a, b) => numericComparer(a, b, 'median_exec_time'),
      },
      {
        id: 'exec_time_change_rate',
        header: 'Ex time change rate',
        filterable: false,
        renderer: execChangeRenderer,
        compareFunc: (a, b) => numericComparer(a, b, 'exec_time_change_rate'),
      },
      {
        id: 'totals',
        header: 'Results',
        renderer: modelRunsTotalsRenderer,
        compareFunc: modelRunsTotalsComparer,
        filterOperators: ['equals', 'notEquals'],
        filterOptionsOverride: resultsColumnFilterOptions,
        filterValueMatcher: resultsColumnFilterMatcher,
        width: 140,
      },
      {
        id: 'status',
        header: 'Last run status',
        renderer: (value) => <StatusChip status={value.status} />,
        filterRenderer: statusFilterRenderer,
      },
      {
        id: 'asset_metadata.owners',
        header: 'Asset Owners',
        defaultUntoggled: true,
        emptyValueLabel: 'No asset owners',
        filterOptionsOverride: getFilterOptionsOverride(
          'asset_metadata.owners',
          isSaaS ? filterOptions : null,
          getItemUniqueFilterOptions(tableData || [], 'owners'),
        ),
        renderer: ({ asset_metadata }) => (
          <AvatarGroup items={asset_metadata?.owners} />
        ),
      },
      {
        id: 'asset_metadata.tags',
        header: 'Asset Tags',
        defaultUntoggled: true,
        emptyValueLabel: 'No asset tags',
        filterOptionsOverride: getFilterOptionsOverride(
          'asset_metadata.tags',
          isSaaS ? filterOptions : null,
          getItemUniqueFilterOptions(tableData || [], 'tags'),
        ),
        renderer: ({ asset_metadata }) => (
          <ChipCounterList items={asset_metadata?.tags} />
        ),
      },
      {
        id: 'asset_metadata.materialization',
        header: 'Materialization',
        defaultUntoggled: true,
        emptyValueLabel: 'No materialization',
        filterOptionsOverride: getFilterOptionsOverride(
          'asset_metadata.materialization',
          isSaaS ? filterOptions : null,
          getItemUniqueFilterOptions(tableData || [], 'materialization'),
        ),
        renderer: ({ asset_metadata }) => (
          <OverflowTooltip>
            {asset_metadata?.materialization
              ? capitalize(asset_metadata.materialization)
              : '-'}
          </OverflowTooltip>
        ),
      },
    ];

    if (isSaaS) {
      const assetColumns: Array<Column<ModelRuns>> = [
        {
          id: 'asset_metadata.is_critical',
          header: 'Critical Asset',
          defaultUntoggled: true,
          filterOptionsOverride: getFilterOptionsOverride(
            'asset_metadata.is_critical',
            filterOptions,
          ),
          renderer: ({ asset_metadata }) => (
            <OverflowTooltip>
              {asset_metadata?.is_critical ? 'Yes' : 'No'}
            </OverflowTooltip>
          ),
        },
        {
          id: 'asset_metadata.subscribers',
          header: 'Asset Subscribers',
          defaultUntoggled: true,
          width: 200,
          emptyValueLabel: 'No asset subscribers',
          filterOptionsOverride: getFilterOptionsOverride(
            'asset_metadata.subscribers',
            filterOptions,
            getItemUniqueFilterOptions(tableData || [], 'subscribers'),
          ),
          renderer: ({ asset_metadata }) => (
            <AvatarGroup items={asset_metadata?.subscribers} />
          ),
        },
        {
          id: 'asset_metadata.path',
          header: 'Asset Path',
          defaultUntoggled: true,
          hideColumnFilter: true,
          filterOperators: ['contains', 'notContains'],
        },
      ];

      return [...baseColumns, ...assetColumns];
    }

    return baseColumns;
  }, [
    filterOptions,
    isSaaS,
    maxExecTime,
    resultsColumnFilterOptions,
    tableData,
  ]);

  return columns;
};

export default useModelRunsTableColumns;
